const QR_IMAGE_SIZE = 512;

const svgToDataUrl = (svg: SVGSVGElement): string => {
  const svgData = new XMLSerializer().serializeToString(svg);
  return "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
};

const drawOnWhiteCanvas = (img: HTMLImageElement, canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): void => {
  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
};

const createCanvas = (): HTMLCanvasElement => {
  const canvas = document.createElement("canvas");
  canvas.width = QR_IMAGE_SIZE;
  canvas.height = QR_IMAGE_SIZE;
  return canvas;
};

/**
 * Copies a rendered QR `svg` to the clipboard as a PNG image. Falls back to copying
 * `fallbackText` when the browser has no image clipboard API (Firefox Android, in-app
 * webviews) or the image write fails.
 *
 * @returns `true` when something was copied, `false` when there was no QR to copy.
 */
export async function copyQrSvgAsPng(svg: SVGSVGElement | null | undefined, fallbackText: string): Promise<boolean> {
  if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) {
    await navigator.clipboard.writeText(fallbackText);
    return true;
  }
  if (!svg) return false;

  // ClipboardItem accepts a Promise so navigator.clipboard.write() is called
  // synchronously within the click handler, preserving user-activation context.
  const blobPromise = new Promise<Blob>((resolve, reject) => {
    const canvas = createCanvas();
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("no canvas context"));
      return;
    }
    const img = new Image();
    img.onload = () => {
      drawOnWhiteCanvas(img, canvas, ctx);
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("canvas toBlob returned null"));
      }, "image/png");
    };
    img.onerror = reject;
    img.src = svgToDataUrl(svg);
  });

  try {
    await navigator.clipboard.write([new ClipboardItem({ "image/png": blobPromise })]);
  } catch {
    await navigator.clipboard.writeText(fallbackText);
  }
  return true;
}

/** Downloads a rendered QR `svg` as a PNG named `fileName`. */
export function downloadQrSvg(svg: SVGSVGElement | null | undefined, fileName = "qr-payjoy.png"): void {
  if (!svg) return;
  const canvas = createCanvas();
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const img = new Image();
  img.onload = () => {
    drawOnWhiteCanvas(img, canvas, ctx);
    const link = document.createElement("a");
    link.download = fileName;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  img.src = svgToDataUrl(svg);
}
